const CATEGORY_ORDER = [
  'sold',
  'sold_price_per_sq_ft',
  'residential_rent',
  'hmo_rent',
  'commercial_rent',
]

const CATEGORY_LABELS = {
  commercial_rent: 'Commercial rent',
  hmo_rent: 'HMO rent',
  residential_rent: 'Comparable rent',
  sold: 'Comparable sale price',
  sold_price_per_sq_ft: 'Comparable price per sq ft',
}

const COMPARABLE_CATEGORY_LABELS = {
  commercial_rent: 'Commercial rent comparables',
  hmo_rent: 'HMO rent comparables',
  residential_rent: 'Residential rent comparables',
  sold: 'Sold comparables',
  sold_price_per_sq_ft: 'Sold price per sq ft comparables',
}

const CURRENCY = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 0,
})

const MONTHLY_RENT_CATEGORIES = new Set(['residential_rent', 'hmo_rent'])
const COVERAGE_RANK = {
  supported: 3,
  contextual: 2,
  limited: 1,
}

function categoryRank(category) {
  const rank = CATEGORY_ORDER.indexOf(category)
  return rank === -1 ? CATEGORY_ORDER.length : rank
}

function areaLabel(value) {
  if (value === 'ft2') return 'sq ft'
  return value
}

export function titleize(value) {
  return value?.toString().replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) || 'Unavailable'
}

export function categoryLabel(category) {
  return CATEGORY_LABELS[category] ?? titleize(category)
}

export function comparableCategoryLabel(category) {
  return COMPARABLE_CATEGORY_LABELS[category] ?? titleize(category)
}

function hasEvidence(category) {
  return (category.observations?.length ?? 0) > 0 ||
    (category.providerAggregates?.length ?? 0) > 0
}

export function presentationCategories(categories, profiles = []) {
  const currentPropertyIds = new Set(
    profiles
      .filter((profile) => profile.kind === 'current_property')
      .map((profile) => profile.id),
  )
  if (currentPropertyIds.size === 0) return categories

  const hasEquivalentEvidence = categories.some((category) => {
    const profileId = category.profileId || 'subject'
    return !currentPropertyIds.has(profileId) && hasEvidence(category)
  })
  if (!hasEquivalentEvidence) return categories

  return categories.filter((category) => (
    !currentPropertyIds.has(category.profileId || 'subject')
  ))
}

function evidenceProfileLabel(profile, fallbackLabel) {
  if (!profile || profile.kind === 'current_property') return fallbackLabel

  const propertyType = profile.physical_property_type || profile.property_type
  const unitType = profile.unit_type || profile.use_type
  const bedroomValue = profile.bedroom_count ?? profile.bedrooms
  const bedroomCount = Number(bedroomValue)
  const hasBedroomCount = bedroomValue !== null && bedroomValue !== undefined && bedroomValue !== ''
  const bedrooms = hasBedroomCount && Number.isInteger(bedroomCount) && bedroomCount >= 0
    ? `${bedroomCount}-bed`
    : null

  if (bedrooms && propertyType) {
    const typeWithoutBedrooms = propertyType.toString().replace(/^\d+[- ]?bed(?:room)?s?\s*/i, '')
    return `${bedrooms} ${titleize(typeWithoutBedrooms || propertyType)}`
  }
  if (propertyType) return titleize(propertyType)
  if (unitType) return titleize(unitType)
  if (bedrooms) return `${bedrooms} ${fallbackLabel}`
  return fallbackLabel
}

function profilePresentation(profileId, profileLabels, profilesById) {
  const profile = profilesById.get(profileId)
  const fallbackLabel = profileLabels[profileId] || 'Evidence profile'
  const profileLabel = evidenceProfileLabel(profile, fallbackLabel)
  const profileKey = !profile || profile.kind === 'current_property'
    ? `profile:${profileId}`
    : `equivalent:${profileLabel.toLowerCase().replaceAll(/\s+/g, ' ').trim()}`

  return { profileId, profileKey, profileLabel }
}

function equivalentProfileCount(profileKey, profileLabels, profiles) {
  const profilesById = new Map(profiles.map((profile) => [profile.id, profile]))
  const equivalent = profiles.filter((profile) => (
    profilePresentation(profile.id, profileLabels, profilesById).profileKey === profileKey
  ))
  const proposed = equivalent.filter((profile) => profile.kind?.startsWith('proposed_'))
  const saved = equivalent.filter((profile) => profile.kind === 'saved_unit')

  return proposed.length || saved.length || equivalent.length || 1
}

function countedProfileLabel(profileLabel, count) {
  return count > 1 ? `${profileLabel} (${count} units)` : profileLabel
}

function categoryStrength(category) {
  const coverage = COVERAGE_RANK[category.coverage] ?? 0
  const freshness = category.freshness === 'fresh' ? 1 : 0
  return (coverage * 10) + freshness
}

export function groupEvidence(categories, profileLabels = {}, profiles = []) {
  const groups = new Map()
  const profilesById = new Map(profiles.map((profile) => [profile.id, profile]))

  categories.forEach((category) => {
    const profileId = category.profileId || 'subject'
    const presentation = profilePresentation(profileId, profileLabels, profilesById)
    if (!groups.has(presentation.profileKey)) {
      const count = equivalentProfileCount(presentation.profileKey, profileLabels, profiles)
      groups.set(presentation.profileKey, {
        profileId,
        profileKey: presentation.profileKey,
        profileLabel: countedProfileLabel(presentation.profileLabel, count),
        categoriesByName: new Map(),
      })
    }
    const group = groups.get(presentation.profileKey)
    const current = group.categoriesByName.get(category.category)
    if (!current || categoryStrength(category) > categoryStrength(current)) {
      group.categoriesByName.set(category.category, category)
    }
  })

  return Array.from(groups.values()).map((group) => ({
    profileId: group.profileId,
    profileKey: group.profileKey,
    profileLabel: group.profileLabel,
    categories: Array.from(group.categoriesByName.values()).toSorted((left, right) => (
      categoryRank(left.category) - categoryRank(right.category)
    )),
  }))
}

export function groupEvidenceProfiles(profileIds, profileLabels = {}, profiles = [], hiddenProfileKeys = []) {
  const groups = new Map()
  const hidden = new Set(hiddenProfileKeys)
  const profilesById = new Map(profiles.map((profile) => [String(profile.id), profile]))

  profileIds.forEach((value) => {
    const profileId = String(value)
    if (!profilesById.has(profileId)) return

    const presentation = profilePresentation(profileId, profileLabels, profilesById)
    if (hidden.has(presentation.profileKey)) return

    if (!groups.has(presentation.profileKey)) {
      groups.set(presentation.profileKey, { ...presentation, profileIds: new Set() })
    }
    groups.get(presentation.profileKey).profileIds.add(profileId)
  })

  return Array.from(groups.values()).map((group) => ({
    profileKey: group.profileKey,
    profileLabel: countedProfileLabel(
      group.profileLabel,
      equivalentProfileCount(group.profileKey, profileLabels, profiles),
    ),
  }))
}

export function formatEvidenceValue(category, evidence) {
  if (evidence.value === null || evidence.value === undefined || evidence.value === '') return 'Unavailable'

  const numericValue = Number(evidence.value)
  if (!Number.isFinite(numericValue)) return 'Unavailable'

  const displaysWeeklyRentMonthly = MONTHLY_RENT_CATEGORIES.has(category) &&
    evidence.period === 'week'
  const displayValue = displaysWeeklyRentMonthly ? numericValue * 52 / 12 : numericValue
  const displayPeriod = displaysWeeklyRentMonthly ? 'month' : evidence.period
  const value = evidence.currency === 'GBP' ? CURRENCY.format(displayValue) : String(displayValue)
  if (category === 'sold_price_per_sq_ft') return `${value} / sq ft`

  const areaUnit = areaLabel(evidence.area_unit || evidence.areaUnit)
  if (areaUnit && displayPeriod) return `${value} / ${areaUnit} / ${displayPeriod}`
  if (areaUnit) return `${value} / ${areaUnit}`
  if (displayPeriod) return `${value} / ${displayPeriod}`
  return value
}

export function aggregateMetadata(aggregate) {
  const parts = []
  const sampleSize = aggregate.sample_size ?? aggregate.sampleSize
  const radius = aggregate.radius_miles ?? aggregate.radiusMiles

  if (sampleSize) parts.push(`${sampleSize} comparables`)
  if (radius) parts.push(`${radius} mile radius`)
  if (aggregate.basis) parts.push(titleize(aggregate.basis))
  return parts.join(' · ')
}

export function receiptLabel(receipt) {
  const retrievedAt = receipt.retrieved_at || receipt.retrievedAt
  const date = retrievedAt ? retrievedAt.slice(0, 10) : 'Retrieval date unavailable'
  return `${titleize(receipt.provider)} · ${retrievedAt ? `retrieved ${date}` : date}`
}
